import React, { useEffect, useState } from "react";
import moment from "moment";
import { Modal } from "react-bootstrap";
import api from "../../api";
import { notify } from "../../utils/toast";

// All pages that can be given to a support member
export const ALL_PAGES = [
  { key: "dashboard",     label: "Dashboard",          icon: "fa-house" },
  { key: "vehicles",      label: "Vehicles",            icon: "fa-bus" },
  { key: "fuel",          label: "Fuel",                icon: "fa-gas-pump" },
  { key: "drivers",       label: "Driver/Employee",     icon: "fa-car" },
  { key: "routes",        label: "Route",               icon: "fa-road" },
  { key: "students",      label: "Students",            icon: "fa-graduation-cap" },
  { key: "fees",          label: "Fees",                icon: "fa-file-invoice-dollar" },
  { key: "feeReceipts",   label: "Fee Receipts",        icon: "fa-receipt" },
  { key: "salary",        label: "Salary",              icon: "fa-money-bill" },
  { key: "loans",         label: "Loans",               icon: "fa-hand-holding-dollar" },
  { key: "paymentMethod", label: "Payment Method",      icon: "fa-credit-card" },
  { key: "maintenance",   label: "Maintenance",         icon: "fa-screwdriver-wrench" },
  { key: "enquiries",     label: "Contact/Enquiries",   icon: "fa-inbox" },
  { key: "bookings",      label: "Bookings",            icon: "fa-calendar-check" },
  { key: "entry",         label: "Entry",               icon: "fa-clipboard-list" },
  { key: "bill",          label: "Bill",                icon: "fa-file-invoice" },
];

export const SupportMembers = () => {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [show, setShow] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const getData = async () => {
    try {
      setLoader(true);
      const res = await api.getSupportMembers();
      if (res?.success) setData(res.members || []);
    } catch (e) {
      notify.error("Failed to load support members");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { getData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this support member?")) return;
    try {
      const res = await api.deleteSupportMember(id);
      if (res?.success) { notify.success("Deleted"); getData(); }
    } catch (e) {
      notify.error("Failed to delete");
    }
  };

  return (
    <>
      <div className="DashboardMain">
        <div className="row">
          <div className="col">
            <h3>Support Members ({data.length})</h3>
            <p style={{ color: "#6b7280", fontSize: 14, marginBottom: 16 }}>
              Add staff who need limited access. Admin controls which pages each member can see.
            </p>

            {loader ? (
              <div className="text-center py-5"><div className="spinner-border" /></div>
            ) : (
              <div className="TableMain mt-3">
                <div className="table-responsive">
                  <table className="table table-hover drivers-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Permissions</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.length > 0 ? data.map((item) => (
                        <tr key={item._id}>
                          <td>
                            <div className="table-text-dark w-max-c db" style={{ fontWeight: 600 }}>
                              {item.name}
                            </div>
                          </td>
                          <td>
                            <span className="table-text-dark db">{item.email}</span>
                          </td>
                          <td>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 300 }}>
                              {item.permissions?.length > 0 ? item.permissions.slice(0, 4).map((p) => {
                                const page = ALL_PAGES.find(pg => pg.key === p);
                                return (
                                  <span key={p} style={{
                                    fontSize: 11, background: "#e8eef6", color: "#002B5B",
                                    padding: "2px 8px", borderRadius: 12, fontWeight: 600,
                                  }}>
                                    {page?.label || p}
                                  </span>
                                );
                              }) : <span style={{ color: "#9ca3af", fontSize: 12 }}>No permissions</span>}
                              {item.permissions?.length > 4 && (
                                <span style={{ fontSize: 11, color: "#6b7280", padding: "2px 6px" }}>
                                  +{item.permissions.length - 4} more
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span style={{
                              display: "inline-block", padding: "3px 10px", borderRadius: 20,
                              fontSize: 12, fontWeight: 600,
                              background: item.isActive ? "#d1fae5" : "#fee2e2",
                              color: item.isActive ? "#065f46" : "#991b1b",
                            }}>
                              {item.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: 13, color: "#6b7280" }}>
                              {moment(item.createdAt).format("MMM Do YYYY")}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-2 align-items-center">
                              <div className="edit p-2 cursor-pointer" onClick={() => setEditItem(item)}>
                                <i className="fa-solid fa-pen-to-square" />
                              </div>
                              <div className="delete p-2 cursor-pointer" onClick={() => handleDelete(item._id)}>
                                <i className="fa-solid fa-trash" />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={6} className="text-center py-4 text-muted">No support members added yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="AddEmpBtn" onClick={() => setShow(true)}><i className="fa-solid fa-plus" /></div>
      </div>

      <SupportMemberModal show={show} handleClose={() => setShow(false)} onSuccess={getData} />
      {editItem && (
        <SupportMemberModal
          show={!!editItem} item={editItem}
          handleClose={() => setEditItem(null)} onSuccess={getData}
        />
      )}
    </>
  );
};

/* ── Create / Edit Modal ── */
const SupportMemberModal = ({ show, handleClose, item, onSuccess }) => {
  const isEdit = !!item;
  const [data, setData] = useState({
    name: item?.name || "",
    email: item?.email || "",
    password: "",
    permissions: item?.permissions || [],
    isActive: item?.isActive !== false,
  });
  const [loader, setLoader] = useState(false);

  const onChange = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const togglePermission = (key) => {
    setData((d) => ({
      ...d,
      permissions: d.permissions.includes(key)
        ? d.permissions.filter((p) => p !== key)
        : [...d.permissions, key],
    }));
  };

  const toggleAll = () => {
    setData((d) => ({
      ...d,
      permissions: d.permissions.length === ALL_PAGES.length
        ? []
        : ALL_PAGES.map((p) => p.key),
    }));
  };

  const handleSubmit = async () => {
    if (!data.name.trim() || !data.email.trim()) {
      notify.error("Name and email are required");
      return;
    }
    if (!isEdit && !data.password.trim()) {
      notify.error("Password is required");
      return;
    }
    setLoader(true);
    try {
      const payload = {
        name: data.name.trim(),
        email: data.email.trim(),
        permissions: data.permissions,
        isActive: data.isActive,
      };
      if (data.password.trim()) payload.password = data.password;

      const res = isEdit
        ? await api.updateSupportMember(item._id, payload)
        : await api.addSupportMember(payload);

      if (res?.success) {
        notify.success(isEdit ? "Member updated!" : "Member added!");
        onSuccess();
        handleClose();
      } else {
        notify.error(res?.message || "Something went wrong");
      }
    } catch (e) {
      notify.error(e?.response?.data?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const allSelected = data.permissions.length === ALL_PAGES.length;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <div className="taskForm" style={{ maxWidth: "100%" }}>
        <div className="closeIcon" onClick={handleClose}><i className="fa-solid fa-xmark" /></div>
        <div className="h3">{isEdit ? "Edit Support Member" : "Add Support Member"}</div>

        <div className="row">
          <div className="col-12 col-md-6">
            <div className="form-group">
              <label>Full Name</label>
              <input type="text" name="name" className="form-control" value={data.name} onChange={onChange} placeholder="e.g. Ali Ahmed" />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" className="form-control" value={data.email} onChange={onChange} placeholder="support@example.com" />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="form-group">
              <label>{isEdit ? "New Password (leave blank to keep current)" : "Password"}</label>
              <input type="password" name="password" className="form-control" value={data.password} onChange={onChange} placeholder={isEdit ? "Leave blank to keep" : "Set password"} />
            </div>
          </div>
          <div className="col-12 col-md-6">
            <div className="form-group">
              <label>Status</label>
              <select name="isActive" className="form-control" value={data.isActive ? "true" : "false"}
                onChange={(e) => setData({ ...data, isActive: e.target.value === "true" })}>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <label style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
              Page Permissions
              <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400, marginLeft: 8 }}>
                ({data.permissions.length} of {ALL_PAGES.length} selected)
              </span>
            </label>
            <button type="button" onClick={toggleAll}
              style={{ padding: "4px 12px", border: "1.5px solid #002B5B", borderRadius: 7, background: "transparent", color: "#002B5B", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              {allSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
            {ALL_PAGES.map((page) => {
              const checked = data.permissions.includes(page.key);
              return (
                <label key={page.key} onClick={() => togglePermission(page.key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                    border: `1.5px solid ${checked ? "#002B5B" : "#e2e8f0"}`,
                    borderRadius: 8, cursor: "pointer",
                    background: checked ? "#e8eef6" : "#f9fafb",
                    transition: "all 0.15s",
                  }}>
                  <input type="checkbox" readOnly checked={checked} style={{ accentColor: "#002B5B", width: 16, height: 16 }} />
                  <i className={`fa-solid ${page.icon}`} style={{ color: checked ? "#002B5B" : "#94a3b8", fontSize: 13, width: 16, textAlign: "center" }} />
                  <span style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: checked ? "#002B5B" : "#374151" }}>
                    {page.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-4">
          <button className="themeBtn w-100 text-center" onClick={handleSubmit} disabled={loader}>
            {loader ? "Saving..." : isEdit ? "Save Changes" : "Add Member"}
          </button>
        </div>
      </div>
    </Modal>
  );
};
